import { ICourse, enStatus, negativeStatus } from 'interface/course';
import { useEffect, useState } from 'react';
import Login from 'components/Login';
import { IAuth } from 'interface/auth';
import { CourseTheme } from 'assets/themes/Course';
import { useAppSelector } from 'hooks/redux';
import { useEmitLessonMessages } from 'hooks/emitLessonMessages';
import { useFacebookPixel } from 'hooks/facebook';
import { useGoogleAnalytics } from 'hooks/google';
import { useParams } from 'react-router';
import { makeStyles, useMediaQuery } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import {
  toggleShowMenu,
  disableMenu,
  enableMenu,
  getLesson,
  enableToastVideoQuality,
  clearLessonState,
} from 'redux/lesson/slice';
import Description from 'components/Lesson/Description';
import Chatroll from 'components/Lesson/Chatroll';
import Chats from 'components/Lesson/Chats';
import SideMenu from 'components/Lesson/SideMenu';
import LessonActions from 'components/Lesson/LessonActions';
import Navigation from 'components/Lesson/Navigation';
import ListDownload from 'components/Lesson/ListDownload';
import ContentArea from './Player';
import Tabs from './Tabs';
import { withCourseTheme } from 'decorators/withCourseTheme';
import { updateLessonOnModule } from 'redux/modules/slice';
import { OfferCarousel } from './OfferCarousel';
import hotjar from 'services/hotjar';
import { IUser } from 'interface/user';

const useStyles = makeStyles<CourseTheme>((theme) => ({
  root: {
    overflow: 'hidden',
  },
  contentContainer: {
    background: theme.variables.courseTheme.background,
    transition: '300ms ease-in-out',
    marginLeft: 0,
    overflowY: 'scroll',
    height: 'calc(100vh - 105px)',
  },
  container: {
    maxWidth: 1260,
    margin: 'auto',
    overflowX: 'hidden',
    padding: theme.spacing(0, 4),
    '& a': {
      color: theme.palette.primary.main,
      textDecoration: 'underline',
    },
  },
}));

const Lesson = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const auth = useAppSelector((state) => state.auth as IAuth);
  const user = useAppSelector((state) => state.user as IUser);
  const lesson = useAppSelector((state) => state.newLesson.lesson);
  const course = useAppSelector((state) => state.course as ICourse);
  const isLessonMenuOpen = useAppSelector((store) => store.newLesson.showLessonMenu);
  const { courseHash, lessonID } = useParams<{ courseHash: string; lessonID: string }>();
  const isSmallScreen = useMediaQuery('@media(max-width: 959px)');
  const dispatch = useDispatch();
  const classes = useStyles();
  useFacebookPixel(course);
  useGoogleAnalytics(course);
  useEmitLessonMessages();

  const handleToggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleOpenMenu = () => {
    setIsMenuOpen(true);
  };

  const handleOpenMenuMobile = () => {
    setIsMobileMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setIsMenuOpen(false);
  };

  const handleCloseMenuMobile = () => {
    if (isLessonMenuOpen) dispatch(toggleShowMenu());
    setIsMobileMenuOpen(false);
  };

  useEffect(() => {
    const timeToWaitExpandExpansionPanel = 2000;
    const timeOut = setTimeout(() => {
      const el = document.getElementById(`lesson-${lessonID}`);
      !!el && el.scrollIntoView({ behavior: 'smooth' });
    }, timeToWaitExpandExpansionPanel);

    return () => {
      clearTimeout(timeOut);
    };
  }, [lessonID]);

  useEffect(() => {
    dispatch(getLesson(parseInt(lessonID)));
    hotjar.emitResearchEventAfterWatchLesson(course.matriculation_date, user.id);
  }, [lessonID, dispatch, course.matriculation_date, user.id]);

  useEffect(() => {
    dispatch(enableMenu());
    return () => {
      dispatch(disableMenu());
    };
  }, [dispatch]);

  useEffect(() => {
    setIsMobileMenuOpen(isLessonMenuOpen);
  }, [isLessonMenuOpen]);

  useEffect(() => {
    if (!course.allow_manual_watch && lesson?.id) {
      dispatch(updateLessonOnModule({ lessonId: lesson.id, data: { watched: true } }));
    }
  }, [lesson, course, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearLessonState());
      dispatch(enableToastVideoQuality());
    };
  }, [dispatch]);
  if (!lesson) return null;

  const isLocked = !lesson.status || !negativeStatus.includes(lesson.status);

  return (
    <div className={classes.root}>
      {!!course && (
        <SideMenu
          open={isMenuOpen}
          mobileOpen={isMobileMenuOpen}
          onOpen={handleOpenMenu}
          onOpenMobile={handleOpenMenuMobile}
          onClose={handleCloseMenu}
          onCloseMobile={handleCloseMenuMobile}
          onToggle={handleToggleMenu}
          courseHash={courseHash}
        />
      )}
      <div
        className={classes.contentContainer}
        style={{ marginLeft: isMenuOpen && !isSmallScreen ? 360 : 0 }}
        id='lesson-main-container'
      >
        <div className={classes.container}>
          {<ContentArea lesson={lesson} courseHash={courseHash} courseId={lesson.id_course} />}
        </div>
        <Navigation
          isAuthenticated={auth.isAuthenticated}
          next={lesson.next}
          prev={lesson.previous}
          status={lesson.status}
        />
        <LessonActions lesson={lesson} />
        {isLocked && (
          <div className={classes.container}>
            <OfferCarousel lessonId={lessonID} />
            <Description
              iframeUrl={lesson.iframe_url}
              iframeConfig={lesson.iframe_config}
              text={lesson.description}
              files={lesson.lesson_files}
              type={lesson.description_type}
            />
            <ListDownload list={lesson.lesson_files} />
            {!(lesson.chats && lesson.chats.haschatroll && lesson.chats.haschatroll !== '0') && <Tabs />}
            {lesson.chats && lesson.chats.haschatroll && lesson.chats.haschatroll !== '0' && (
              <Chatroll code={lesson.chats.chatroll} chatType={lesson.chats.chatroll_tipo} />
            )}
          </div>
        )}
        {lesson.status === enStatus.BLOCKED && !auth.isAuthenticated && <Login />}
        {lesson.chats && <Chats chats={lesson.chats} />}
      </div>
    </div>
  );
};

export default withCourseTheme(Lesson);
